import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { MoreVertIcon } from "icons";

import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import { MenuItem } from "components/Menu";
import Text from "components/Text";

import { useLoggedUserIsDealAdmin } from "entities/Deal/hooks";
import { useDealChecklistPermission } from "entities/Deal/sdk";
import DropdownFieldInstructionsDialog from "entities/Field/components/DropdownFieldInstructionsDialog";
import DropdownFieldRemarksDialog from "entities/Field/components/DropdownFieldRemarksDialog";
import FieldHistoryDialog from "entities/Field/components/FieldHistoryDialog";
import { IField } from "entities/Field/sdk";
import ShareDialog from "entities/Package/components/ShareDialog";
import ShareWithButton from "entities/Package/components/ShareWithButton";
import DueDiligenceFieldNewTaskButton from "entities/Task/components/DueDiligenceFieldNewTaskButton";

interface IFieldDropdownMenu {
  field: IField;
  packageId: number;
  dealId: number;
  categoryId: number;
  sectionId: number;
  tabId: number;
  onUpdate: ({ message }: { message?: string }) => void;
  isHidden: boolean;
  showHistoryButton?: boolean;
  menuButtonDataTestid?: string;
}

const FieldDropdownMenu = ({
  dealId,
  packageId,
  field,
  categoryId,
  sectionId,
  tabId,
  onUpdate,
  isHidden, // Used to indicate whether the field should be visible or not. We do not want to conditionally render the whole component as it would cause redundant data fetches.
  menuButtonDataTestid,
  showHistoryButton = true,
}: IFieldDropdownMenu) => {
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [showValuesHistoryDialog, setShowValuesHistoryDialog] =
    useState<boolean>(false);
  const [showInstructionsDialog, setShowInstructionsDialog] =
    useState<boolean>(false);
  const [showRemarksDialog, setShowRemarksDialog] = useState<boolean>(false);

  const { loggedUserIsDealAdmin } = useLoggedUserIsDealAdmin({ dealId });
  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });

  const hasFullAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.is_full_access,
    [userPermission]
  );

  const showRemarksAndInstructionsAction = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const handleOpenInstructions = useCallback(
    () => setShowInstructionsDialog(true),
    [setShowInstructionsDialog]
  );

  const handleCloseInstructions = useCallback(() => {
    setShowInstructionsDialog(false);
  }, [setShowInstructionsDialog]);

  const handleOpenRemarks = useCallback(
    () => setShowRemarksDialog(true),
    [setShowRemarksDialog]
  );

  const handleCloseRemarks = useCallback(() => {
    setShowRemarksDialog(false);
  }, [setShowRemarksDialog]);

  const sharedRemark = useMemo(
    () => _.first(field.shared_remarks),
    [field.shared_remarks]
  );

  const privateRemark = useMemo(
    () => _.first(field.private_remarks),
    [field.private_remarks]
  );

  if ((!hasFullAccess && !showRemarksAndInstructionsAction) || isHidden) {
    return null;
  }

  return (
    <>
      <ButtonDropdown
        variant="text"
        label={<MoreVertIcon />}
        width="auto"
        size="small"
        sx={{ padding: "2px" }}
        buttonDataTestid={menuButtonDataTestid}
      >
        {hasFullAccess && [
          <GroupLabel key="actions-label" label="Actions" />,

          <DueDiligenceFieldNewTaskButton
            key="new-task-button"
            dealId={dealId}
            companyField={field}
            packageId={packageId}
            sectionId={sectionId}
            categoryId={categoryId}
            tabId={tabId}
            menuOptionDataTestid="create-new-task-option"
          />,

          loggedUserIsDealAdmin && (
            <ShareWithButton
              key="share-button"
              initiallySelectedItemIds={[field.field_id]}
              onClick={() => setShowShareDialog(true)}
            />
          ),

          showHistoryButton && (
            <MenuItem
              key="show-history"
              onClick={() => setShowValuesHistoryDialog(true)}
              dataTestid="show-history-option"
            >
              <Text variant="text2" data-testid="action-menu-option-name">
                Show field values history
              </Text>
            </MenuItem>
          ),
        ]}
        {showRemarksAndInstructionsAction && [
          <GroupLabel key="information-label" label="Information" />,

          <MenuItem
            key="instructions"
            onClick={handleOpenInstructions}
            dataTestid="instructions-option"
          >
            <Text variant="text2" data-testid="action-menu-option-name">
              Instructions
            </Text>
          </MenuItem>,
          <MenuItem
            key="remarks"
            onClick={handleOpenRemarks}
            dataTestid="remarks-option"
          >
            <Text variant="text2" data-testid="action-menu-option-name">
              Remarks
            </Text>
          </MenuItem>,
        ]}
      </ButtonDropdown>

      {hasFullAccess && !_.isNil(userPermission) && (
        <>
          {showValuesHistoryDialog && (
            <FieldHistoryDialog
              dealId={dealId}
              field={field}
              onClose={() => setShowValuesHistoryDialog(false)}
            />
          )}

          {showShareDialog && (
            <ShareDialog
              open
              handleClose={() => setShowShareDialog(false)}
              initiallySelectedItemIds={[field.field_id]}
            />
          )}
        </>
      )}

      {showRemarksAndInstructionsAction && !_.isNil(userPermission) && (
        <>
          <DropdownFieldRemarksDialog
            open={showRemarksDialog}
            handleClose={handleCloseRemarks}
            dealId={dealId}
            fieldId={field.field_id}
            onUpdate={onUpdate}
            sharedRemark={sharedRemark}
            privateRemark={privateRemark}
            fieldName={field.display_name}
            showPrivateRemarks={
              userPermission.is_checklist_owner && hasFullAccess
            }
            packageId={packageId}
          />
          <DropdownFieldInstructionsDialog
            open={showInstructionsDialog}
            handleClose={handleCloseInstructions}
            dealId={dealId}
            packageId={packageId}
            fieldId={field.field_id}
            showPrivateInstructions={
              userPermission.is_checklist_owner && hasFullAccess
            }
          />
        </>
      )}
    </>
  );
};

export default FieldDropdownMenu;
